import { type ReactElement, useEffect, useRef, useState } from "react";
import { useFlux } from "../direct/use-flux";
import { lireCle } from "./cle";
import {
  OVERLAY_ATTENTE,
  OVERLAY_CLE_ABSENTE,
  OVERLAY_CLE_REFUSEE,
  OVERLAY_ENVOI_ECHEC,
  OVERLAY_HORS_OBS,
  overlayEnvoyee,
  overlayFlux,
  SON_ESSAI,
  SOURCE_ESSAI,
} from "./essai-texts";
import { nomDeScene, obsDeLaPage } from "./obs";

/**
 * L'overlay d'essai (`/overlay/essai#cle=...`) : la page qu'on ajoute comme source navigateur d'OBS pour
 * verifier, avant tout le reste de Q1, que le navigateur d'OBS tient ce que le lot suppose.
 *
 * - **Il sait la scene active.** `window.obsstudio` n'existe que dans le navigateur d'OBS. Au chargement la
 *   page demande `getCurrentScene` ; ensuite elle ecoute `obsSceneChanged`. Chaque scene est relayee a
 *   Castellan par `POST /api/overlay/scene`, avec la cle en en-tete (`Authorization: Cle <cle>`) : la
 *   regie la montre dans sa carte « Essai d'OBS ». Cela ne passe pas par le flux temps reel (PR C), pour que
 *   l'essai ne depende pas du moteur.
 * - **Il ecoute le flux** (`/api/overlay/flux?nom=essai`) et montre l'etat de la connexion et le dernier message
 *   recu : c'est la preuve, dans OBS, que le moteur de la PR C tient. Ce bavardage est pour l'essai seulement ;
 *   l'overlay `scene` (`scene.tsx`), lui, ne montre rien.
 * - **Il joue un son** (`SON_ESSAI`) a chaque changement de scene, pas au chargement : le navigateur d'OBS
 *   le laisse partir, et on l'entend dans le mixeur si « Controler l'audio via OBS » est coche.
 * - **Hors d'OBS** (un navigateur ordinaire), il le dit et ne poste rien.
 *
 * Cette page est visible et bavarde parce qu'elle ne sert qu'a l'essai. Elle ne montre jamais la cle.
 */

const URL_FLUX = "/api/overlay/flux?nom=essai";

type Etat =
  | { kind: "attente" }
  | { kind: "envoyee"; nom: string }
  | { kind: "refus"; texte: string };

export function EssaiOverlay(): ReactElement {
  // La cle est lue une fois, au premier rendu : elle quitte alors l'adresse.
  const [cle] = useState(() => lireCle());
  const [obs] = useState(() => obsDeLaPage());
  const [etat, setEtat] = useState<Etat>({ kind: "attente" });
  const actif = useRef(true);
  // Le flux n'est ouvert que dans OBS et avec une cle : hors d'OBS la page ne signale rien, pas meme sa presence.
  const { etat: etatFlux, dernier } = useFlux(
    cle !== null && obs !== undefined ? URL_FLUX : null,
    cle === null ? undefined : { authorization: `Cle ${cle}` },
  );

  useEffect(() => {
    actif.current = true;
    if (cle === null || obs === undefined) return;

    /** Poste une scene a Castellan ; le resultat ne se dit que si la page est encore la. */
    async function poster(nom: string): Promise<void> {
      let reponse: Response;
      try {
        reponse = await fetch("/api/overlay/scene", {
          method: "POST",
          headers: { authorization: `Cle ${cle}`, "content-type": "application/json" },
          body: JSON.stringify({ nom, source: SOURCE_ESSAI }),
        });
      } catch {
        if (actif.current) setEtat({ kind: "refus", texte: OVERLAY_ENVOI_ECHEC });
        return;
      }
      if (!actif.current) return;
      if (reponse.ok) setEtat({ kind: "envoyee", nom });
      else if (reponse.status === 401) setEtat({ kind: "refus", texte: OVERLAY_CLE_REFUSEE });
      else setEtat({ kind: "refus", texte: OVERLAY_ENVOI_ECHEC });
    }

    function surChangement(evenement: Event): void {
      const nom = nomDeScene(evenement);
      if (nom === null) return;
      void poster(nom);
      // Un son refuse ou un fichier absent ne doit rien casser : l'essai le dira par l'oreille.
      try {
        void Promise.resolve(new Audio(SON_ESSAI).play()).catch(() => undefined);
      } catch {
        // Pas d'audio dans ce navigateur : rien a jouer.
      }
    }

    window.addEventListener("obsSceneChanged", surChangement);
    obs.getCurrentScene?.((scene) => {
      if (typeof scene.name === "string" && scene.name !== "") void poster(scene.name);
    });
    return () => {
      actif.current = false;
      window.removeEventListener("obsSceneChanged", surChangement);
    };
  }, [cle, obs]);

  let texte: string;
  if (cle === null) texte = OVERLAY_CLE_ABSENTE;
  else if (obs === undefined) texte = OVERLAY_HORS_OBS;
  else if (etat.kind === "envoyee") texte = overlayEnvoyee(etat.nom);
  else if (etat.kind === "refus") texte = etat.texte;
  else texte = OVERLAY_ATTENTE;

  return (
    <div className="m-0 p-2 font-mono text-sm text-white">
      <p className="m-0" role="status">
        {texte}
      </p>
      {cle !== null && obs !== undefined ? (
        <p className="m-0" data-testid="flux-essai">
          {overlayFlux(etatFlux.genre, dernier?.genre ?? null)}
        </p>
      ) : null}
    </div>
  );
}
